<template>
  <span v-if="enabled">
    <v-btn text color="#6ec39b" class="tw-ml-2" @click="open">
      <v-icon left>mdi-file-excel</v-icon>
      ดาวน์โหลด Excel
    </v-btn>

    <v-dialog v-model="dialog" max-width="640" scrollable :persistent="building">
      <v-card class="lao-export">
        <v-card-title class="tw-flex tw-items-center tw-gap-3">
          <v-avatar size="36" color="#e8f5ee">
            <v-icon color="#3f9e73">mdi-file-excel-outline</v-icon>
          </v-avatar>
          <div class="tw-leading-tight">
            <div class="tw-text-lg tw-font-bold">ดาวน์โหลด Excel</div>
            <div class="tw-text-xs tw-text-gray-500 tw-font-normal">
              Lao eTax · {{ typeName }} · 1 แถวต่อ 1 เอกสาร
            </div>
          </div>
          <v-spacer />
          <v-btn icon small :disabled="building" aria-label="ปิด" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="tw-pt-5">
          <!-- ── Date range ─────────────────────────────────────────── -->
          <div class="lao-export__label">ช่วงวันที่เอกสาร</div>
          <v-chip-group v-model="preset" mandatory column class="tw-mb-1">
            <v-chip
              v-for="p in presets"
              :key="p.key"
              :value="p.key"
              small
              filter
              outlined
              active-class="lao-export__chip--on"
              :disabled="building"
            >
              {{ p.label }}
            </v-chip>
            <v-chip value="custom" small filter outlined active-class="lao-export__chip--on" :disabled="building">
              กำหนดเอง
            </v-chip>
          </v-chip-group>
          <!-- Same picker as date-range-search.vue, the app's other document
               date range: a read-only field that opens a v-date-picker. -->
          <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
            <v-menu
              v-model="fromMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              max-width="292px"
              min-width="292px"
              :disabled="building"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="from"
                  label="จากวันที่เอกสาร"
                  append-icon="mdi-calendar-month-outline"
                  readonly
                  dense
                  outlined
                  hide-details
                  :disabled="building"
                  v-on="on"
                />
              </template>
              <v-date-picker
                :value="from"
                color="primary"
                locale="en-in"
                no-title
                @input="pickFrom"
              />
            </v-menu>
            <v-menu
              v-model="toMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              max-width="292px"
              min-width="292px"
              :disabled="building"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="to"
                  label="ถึงวันที่"
                  append-icon="mdi-calendar-month-outline"
                  readonly
                  dense
                  outlined
                  hide-details
                  :disabled="building"
                  v-on="on"
                />
              </template>
              <v-date-picker
                :value="to"
                :min="from"
                color="primary"
                locale="en-in"
                no-title
                @input="pickTo"
              />
            </v-menu>
          </div>

          <!-- ── Status ─────────────────────────────────────────────── -->
          <div class="tw-flex tw-items-center tw-mt-5">
            <div class="lao-export__label tw-mb-0">สถานะการนำส่ง</div>
            <v-spacer />
            <button
              v-if="statuses.length"
              type="button"
              class="tw-text-xs tw-text-gray-500 hover:tw-underline"
              :disabled="building"
              @click="statuses = []"
            >
              เลือกทั้งหมด
            </button>
          </div>
          <div class="tw-text-xs tw-text-gray-500 tw-mb-1">ไม่เลือก = ทุกสถานะ · ตัวเลขคือจำนวนในช่วงวันที่นี้</div>
          <v-chip-group v-model="statuses" multiple column>
            <v-chip
              v-for="s in statusItems"
              :key="s.value"
              :value="s.value"
              small
              outlined
              :disabled="building || (loaded && !summary.byStatus[s.value] && statuses.indexOf(s.value) === -1)"
              active-class="lao-export__chip--on"
            >
              <span class="lao-export__dot" :style="{ background: s.color }" />
              {{ s.text }}
              <span class="tw-ml-1 tw-text-gray-500">{{ loaded ? summary.byStatus[s.value] || 0 : "–" }}</span>
            </v-chip>
          </v-chip-group>

          <!-- ── Summary / progress ─────────────────────────────────── -->
          <div class="lao-export__summary tw-mt-4" aria-live="polite">
            <template v-if="rangeError">
              <v-icon small color="orange darken-2" class="tw-mr-1">mdi-alert-outline</v-icon>
              {{ rangeError }}
            </template>
            <template v-else-if="waiting">
              <div class="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                <div class="tw-text-sm tw-flex-1">
                  ช่วงนี้ยาวกว่า 3 เดือน เอกสารอาจมากและใช้เวลาโหลดสักครู่ —
                  โหลดเพื่อดูจำนวนก่อนดาวน์โหลด
                </div>
                <v-btn small depressed color="#e8f5ee" class="tw-text-green-800" @click="reload(true)">
                  <v-icon left small>mdi-database-search-outline</v-icon>
                  โหลดข้อมูล
                </v-btn>
              </div>
            </template>
            <template v-else-if="loading">
              <div class="tw-text-sm tw-mb-2">
                กำลังโหลดเอกสาร… {{ loadedCount.toLocaleString() }} ใบ
                <span v-if="longRange" class="tw-text-xs tw-text-gray-500">· ช่วงยาวอาจใช้เวลา 1–2 นาที</span>
              </div>
              <v-progress-linear indeterminate color="#6ec39b" height="6" rounded />
            </template>
            <template v-else-if="loadError">
              <v-icon small color="error" class="tw-mr-1">mdi-alert-circle-outline</v-icon>
              โหลดไม่สำเร็จ — {{ loadError }}
              <v-btn x-small text color="#3f9e73" @click="reload">ลองใหม่</v-btn>
            </template>
            <template v-else-if="truncated">
              <v-icon small color="orange darken-2" class="tw-mr-1">mdi-alert-outline</v-icon>
              ช่วงนี้มีเอกสารเกิน {{ exportLimit.toLocaleString() }} ใบ — เลือกช่วงวันที่ให้แคบลง
            </template>
            <template v-else-if="building">
              <div class="tw-text-sm tw-mb-2">
                {{ buildProgress >= 100 ? "กำลังบันทึกไฟล์…" : "กำลังสร้างไฟล์… " + buildProgress + "%" }}
              </div>
              <v-progress-linear :value="buildProgress" color="#6ec39b" height="6" rounded />
            </template>
            <template v-else-if="loaded">
              <div class="tw-flex tw-items-end tw-justify-between tw-gap-4 tw-flex-wrap">
                <div>
                  <div class="tw-text-xs tw-text-gray-500">จะดาวน์โหลด</div>
                  <div class="tw-text-2xl tw-font-bold tw-leading-none">
                    {{ summary.count.toLocaleString() }}
                    <span class="tw-text-sm tw-font-normal tw-text-gray-600">เอกสาร</span>
                  </div>
                </div>
                <div class="tw-text-right">
                  <div class="tw-text-xs tw-text-gray-500">ยอดรวมสุทธิ (ใบลดหนี้หักออก)</div>
                  <div class="tw-text-lg tw-font-bold tw-leading-none">{{ formatMoney(summary.net) }}</div>
                </div>
              </div>
              <div v-if="!summary.count" class="tw-text-xs tw-text-gray-500 tw-mt-2">
                ไม่มีเอกสารตามเงื่อนไขนี้
              </div>
              <div v-if="buildError" class="tw-text-xs tw-text-red-600 tw-mt-2">
                สร้างไฟล์ไม่สำเร็จ — {{ buildError }}
              </div>
              <div v-if="done" class="tw-text-xs tw-text-green-700 tw-mt-2">
                <v-icon x-small color="green darken-1">mdi-check-circle</v-icon>
                ดาวน์โหลดแล้ว — {{ fileName }}
              </div>
            </template>
          </div>
        </v-card-text>

        <v-divider />
        <v-card-actions class="tw-px-6 tw-py-3">
          <div class="tw-text-xs tw-text-gray-500 tw-truncate tw-hidden sm:tw-block">
            {{ loadedAt ? "ข้อมูล ณ " + loadedAt : "" }}
          </div>
          <v-spacer />
          <v-btn text :disabled="building" @click="dialog = false">ยกเลิก</v-btn>
          <v-btn
            depressed
            color="#6ec39b"
            class="white--text"
            :loading="building"
            :disabled="!canDownload"
            @click="exportExcel"
          >
            <v-icon left small>mdi-download</v-icon>
            ดาวน์โหลด{{ loaded && summary.count ? " (" + summary.count.toLocaleString() + ")" : "" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
/**
 * Excel export of a company's Lao eTax documents by date range and submission
 * status — one row per document, of the doc type of the page it sits on (the
 * same scope as the นำส่ง button beside it).
 *
 * Shows the result BEFORE the download: the date range is read (in pages, with
 * a running count) as soon as it changes, and the status chips, count and net
 * total are computed from that read, so nobody downloads an empty or wrong
 * file to find out. Status and type are filtered in the browser (see
 * libs/laoEtaxExport.js for why), so changing them costs no reads.
 *
 * Shown only to companies switched on in Settings/laoEtax.exportEnabled
 * (constants/laoEtaxExport.js; toggled from the ตั้งค่า Lao eTax page). The
 * approved page keeps its own monthly register export.
 */
import * as XLSX from "xlsx";
import moment from "moment";
import fc from "@/firebaseConfig";
import { resolveLaoExportEnabled } from "@/constants/laoEtaxExport";
import { resolveLaoExtraColumns } from "@/constants/laoEtaxColumns";
import {
  EXPORT_LIMIT,
  DOC_TYPE_CODE,
  statusOptions,
  datePresets,
  exportDateBounds,
  fetchExportRows,
  needsManualLoad,
  toExportRow,
  filterRows,
  summarize,
  buildSheet,
} from "@/libs/laoEtaxExport";

const DOC_TYPE_NAME = { 1: "ใบกำกับภาษี", 4: "ใบลดหนี้", 6: "ใบเสร็จ/ใบกำกับภาษี" };
// Rows written per tick while building, so a big file keeps the page alive
// and the bar moving.
const CHUNK = 500;
const RELOAD_DEBOUNCE_MS = 400;
const nextTick = () => new Promise((resolve) => setTimeout(resolve, 0));

export default {
  name: "LaoEtaxExportDialog",
  props: {
    companyId: { type: String, required: true },
    // @DocType of the page the button sits on — the export's scope.
    docType: { type: Number, default: null },
    // Fallback scope only when the page has no doc type.
    allowedDocTypes: { type: Array, default: () => [] },
  },
  data() {
    const presets = datePresets();
    return {
      enabled: false,
      extraColumns: [],
      dialog: false,
      presets,
      preset: "thisMonth",
      from: presets[0].from,
      to: presets[0].to,
      statuses: [],
      fromMenu: false,
      toMenu: false,
      statusItems: statusOptions(),
      exportLimit: EXPORT_LIMIT,
      // The last read of the date range.
      allRows: [],
      loaded: false,
      // A long range waiting for its "โหลดข้อมูล" click.
      waiting: false,
      loading: false,
      loadedCount: 0,
      loadError: "",
      truncated: false,
      loadedAt: "",
      readToken: 0,
      reloadTimer: null,
      // The download.
      building: false,
      buildProgress: 0,
      done: false,
      buildError: "",
      fileName: "",
    };
  },
  computed: {
    typeName() {
      return DOC_TYPE_NAME[this.docType] || "ทุกประเภทที่ยื่น";
    },
    selectedDocTypes() {
      return this.docType ? [this.docType] : this.allowedDocTypes.slice();
    },
    rangeError() {
      return exportDateBounds(this.from, this.to).error || "";
    },
    longRange() {
      return needsManualLoad(this.from, this.to);
    },
    // Narrowed to the page's doc type only: the chips count every status in it.
    typedRows() {
      return filterRows(this.allRows, [], this.selectedDocTypes);
    },
    summary() {
      return summarize(this.typedRows, this.statuses);
    },
    canDownload() {
      return this.loaded && !this.loading && !this.truncated && !this.building && this.summary.count > 0;
    },
  },
  watch: {
    companyId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
    preset(key) {
      const p = this.presets.find((x) => x.key === key);
      if (p) {
        this.from = p.from;
        this.to = p.to;
      }
    },
    from() {
      this.scheduleReload();
    },
    to() {
      this.scheduleReload();
    },
    statuses() {
      this.done = false;
    },
    // Closing stops a read in flight — a year's range is a download nobody
    // is waiting for once the dialog is gone. open() reads again.
    dialog(isOpen) {
      if (isOpen) return;
      clearTimeout(this.reloadTimer);
      this.readToken += 1;
      this.loading = false;
    },
  },
  beforeDestroy() {
    clearTimeout(this.reloadTimer);
    this.readToken += 1;
  },
  methods: {
    async load() {
      this.enabled = false;
      if (!this.companyId) return;
      const [enabled, extra] = await Promise.all([
        resolveLaoExportEnabled(fc.db, this.companyId),
        resolveLaoExtraColumns(fc.db, this.companyId),
      ]);
      this.extraColumns = extra;
      this.enabled = enabled;
    },
    open() {
      this.done = false;
      this.buildError = "";
      // Every open starts fresh — this month, every status — like the app's
      // other export dialogs. Keeping the last choice read as stale data: a
      // dialog reopened on "เดือนที่แล้ว" looks like it never refreshed. The
      // presets are recomputed too, for a page left open past a month end.
      this.presets = datePresets();
      this.statuses = [];
      this.preset = this.presets[0].key;
      this.from = this.presets[0].from;
      this.to = this.presets[0].to;
      this.dialog = true;
      // After the from/to watchers have run: their scheduleReload would read
      // the range a second time 400 ms later, and reload() clears that timer.
      this.$nextTick(() => this.reload());
    },
    // A date picked by hand is a custom range, whatever chip was lit.
    pickFrom(v) {
      this.fromMenu = false;
      this.preset = "custom";
      this.from = v;
    },
    pickTo(v) {
      this.toMenu = false;
      this.preset = "custom";
      this.to = v;
    },
    scheduleReload() {
      this.done = false;
      if (!this.dialog) return;
      clearTimeout(this.reloadTimer);
      this.reloadTimer = setTimeout(this.reload, RELOAD_DEBOUNCE_MS);
    },
    // Read the date range. A newer read supersedes an older one still in
    // flight — readToken — so a slow "ปีนี้" cannot land on top of "เดือนนี้".
    // A range past AUTO_LOAD_MAX_DAYS waits for the button (force) instead of
    // starting a large download because a chip was clicked.
    async reload(force) {
      clearTimeout(this.reloadTimer);
      const token = ++this.readToken;
      this.loaded = false;
      this.loading = false;
      this.waiting = false;
      this.loadError = "";
      this.truncated = false;
      this.allRows = [];
      const bounds = exportDateBounds(this.from, this.to);
      if (bounds.error) return;
      if (force !== true && this.longRange) {
        this.waiting = true;
        return;
      }
      this.loading = true;
      this.loadedCount = 0;
      try {
        const res = await fetchExportRows(fc.db, this.companyId, bounds, {
          docType: this.docType,
          toRow: (id, data) =>
            data["@OverQuota"] === true ? null : toExportRow(id, data, this.extraColumns),
          onProgress: (n) => {
            if (token === this.readToken) this.loadedCount = n;
          },
          isStale: () => token !== this.readToken,
        });
        if (!res || token !== this.readToken) return;
        this.truncated = res.truncated;
        this.allRows = res.rows;
        this.loadedAt = moment().format("HH:mm");
        this.loaded = true;
      } catch (e) {
        if (token === this.readToken) this.loadError = (e && e.message) || String(e);
      } finally {
        if (token === this.readToken) this.loading = false;
      }
    },
    formatMoney(n) {
      return (Number(n) || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    async exportExcel() {
      if (!this.canDownload) return;
      this.building = true;
      this.buildProgress = 0;
      this.done = false;
      this.buildError = "";
      try {
        // Frozen at click time; the chips can't change mid-build (disabled).
        const rows = filterRows(this.typedRows, this.statuses, []);
        const sheet = buildSheet(rows, this.extraColumns);
        const ws = XLSX.utils.aoa_to_sheet([sheet.header]);
        const body = sheet.aoa.slice(1);
        for (let i = 0; i < body.length; i += CHUNK) {
          XLSX.utils.sheet_add_aoa(ws, body.slice(i, i + CHUNK), { origin: -1 });
          // Building is the bulk of the work; the last 5% is the save.
          this.buildProgress = Math.round((Math.min(i + CHUNK, body.length) / body.length) * 95);
          await nextTick();
        }
        for (let r = 1; r < sheet.aoa.length; r++) {
          sheet.amountCols.forEach((c) => {
            const cell = ws[XLSX.utils.encode_cell({ r, c })];
            if (cell && typeof cell.v === "number") cell.z = "#,##0.00";
          });
        }
        // The note is free text; its header is short but its cells are not.
        ws["!cols"] = sheet.header.map((h, i) => ({
          wch:
            sheet.amountCols.indexOf(i) !== -1
              ? 22
              : h === "หมายเหตุ"
              ? 40
              : Math.max(12, h.length + 4),
        }));
        this.buildProgress = 100;
        await nextTick();
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Lao eTax");
        const scope = DOC_TYPE_CODE[this.docType] || "all";
        this.fileName = `lao-etax_${scope}_${this.from}_${this.to}.xlsx`;
        XLSX.writeFile(wb, this.fileName);
        this.done = true;
      } catch (e) {
        this.buildError = (e && e.message) || String(e);
      } finally {
        this.building = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.lao-export__label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}
.lao-export__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}
.lao-export__chip--on {
  border-color: #3f9e73 !important;
  background: #e8f5ee !important;
  color: #1f6b4a !important;
}
.lao-export__summary {
  background: #f7f9f8;
  border: 1px solid #e5ebe8;
  border-radius: 10px;
  padding: 14px 16px;
  min-height: 72px;
}
</style>
